import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { saveCostTableDraft } from "../Api";
import { extractDraftRows } from "./costTableDraft";

/**
 * The cost table's single writer of quote.costTableDraft.
 *
 * a1cf453 stopped the operator's pricing being wiped. It left three gaps that
 * this hook closes:
 *
 *   - failures were reported with console.error only, so a 401 and a success
 *     looked identical from the operator's chair;
 *   - the debounce was cleared on unmount, so collapsing the lane within 800ms
 *     of the last keystroke dropped that write;
 *   - nothing ever said "saved", leaving close-and-reopen as the only way to
 *     find out — the very action that was losing the work.
 *
 * Autosave stays as the safety net. `saveNow` is the operator's explicit
 * instruction, and it reports what happened.
 *
 * Rates are NOT persisted here and never should be: they stay authoritative on
 * the saved airline/agent and are recomputed on load, so a stale draft cannot
 * re-offer a price we no longer honour.
 */
export default function useCostTableDraft({
  quoteId,
  loginToken,
  role,
  optionGroups,
  selectedScreening,
  armed,
  initialSavedAt = null,
  debounceMs = 800,
  savedFlashMs = 2500,
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(
    initialSavedAt ? new Date(initialSavedAt) : null
  );
  const [justSaved, setJustSaved] = useState(false);
  /* Bumped after every write attempt so the dirty comparison below re-runs
     against the new baseline without the caller having to re-render. */
  const [, setBaselineTick] = useState(0);

  const rows = useMemo(
    () => extractDraftRows(optionGroups, selectedScreening),
    [optionGroups, selectedScreening]
  );
  const serial = useMemo(() => JSON.stringify(rows), [rows]);

  /* What we believe is in the database. Null until a load has produced options
     — before that, "no rows" means "not loaded yet", not "the operator cleared
     everything", and treating the two alike is what wrote [] over good data. */
  const persistedRef = useRef(null);
  /* Count the options themselves, not the groups. A quote with rated agents but
     no rated airline produces [[], []] — outer length 2, nothing in it — which
     a length check on the outer array reads as "loaded". */
  const optionCount = useMemo(
    () =>
      (Array.isArray(optionGroups) ? optionGroups : []).reduce(
        (n, g) => n + (Array.isArray(g) ? g.length : 0),
        0
      ),
    [optionGroups]
  );
  const canPersist = Boolean(armed && optionCount > 0 && quoteId && loginToken);

  if (canPersist && persistedRef.current === null) {
    // The table has just been rehydrated from the stored draft, so what is on
    // screen IS what is saved. Established during render so the very first
    // paint reads "clean" rather than flashing "Unsaved changes".
    persistedRef.current = serial;
  }

  const isDirty = canPersist && persistedRef.current !== serial;

  /* The payload that has not yet reached the database. Held in a ref so the
     unmount flush can reach it without re-subscribing. */
  const pendingRef = useRef(null);
  const timerRef = useRef(null);
  /* A draft whose autosave failed. Without this the effect would reschedule
     immediately on the resulting state change and retry every 800ms forever. */
  const autosaveFailedForRef = useRef(null);

  const latest = useRef({});
  latest.current = { quoteId, loginToken, role, rows, serial, canPersist };

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const write = useCallback(async ({ manual }) => {
    const { quoteId: id, loginToken: token, role: r, rows: payload, serial: sig } = latest.current;
    clearTimer();
    /* Never write on behalf of a table that has nothing loaded. "The operator
       cleared everything" and "the fetch failed" produce the same empty rows,
       and writing [] in that state is what destroyed the draft in the field. */
    if (!latest.current.canPersist) {
      return { ok: false, error: "There is nothing loaded to save yet." };
    }
    setSaving(true);
    try {
      await saveCostTableDraft({ token, role: r, id, rows: payload });
      persistedRef.current = sig;
      pendingRef.current = null;
      autosaveFailedForRef.current = null;
      setError(null);
      setLastSavedAt(new Date());
      setJustSaved(true);
      setBaselineTick((n) => n + 1);
      return { ok: true };
    } catch (err) {
      /* A failed write must never become the new baseline — the unsaved rows
         have to stay unsaved, or the next edit would look like the only
         outstanding change and the earlier work would never be retried. */
      const reason =
        err?.response?.data?.message || err?.message || "Could not save the cost table";
      if (!manual) autosaveFailedForRef.current = sig;
      setError(reason);
      setBaselineTick((n) => n + 1);
      return { ok: false, error: reason };
    } finally {
      setSaving(false);
    }
  }, []);

  // Autosave: the safety net under the button.
  useEffect(() => {
    clearTimer();
    if (!canPersist || !isDirty) {
      if (!isDirty) pendingRef.current = null;
      return undefined;
    }
    pendingRef.current = { rows, serial };
    if (autosaveFailedForRef.current === serial) {
      // Already tried and failed for exactly this draft. Wait for either a
      // further edit or the operator pressing Save.
      return undefined;
    }
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      write({ manual: false });
    }, debounceMs);
    return clearTimer;
  }, [canPersist, isDirty, rows, serial, debounceMs, write]);

  /* Unmount flush. Deliberately a separate mount-scoped effect: the cleanup of
     the effect above runs on every dependency change, where cancelling is
     correct. Only here does cancelling mean losing the write. */
  useEffect(() => {
    return () => {
      clearTimer();
      if (!pendingRef.current) return;
      const { quoteId: id, loginToken: token, role: r, rows: payload } = latest.current;
      if (!id || !token) return;
      // Fire and forget — the component is going away and there is nobody left
      // to tell. Strictly better than the guaranteed loss it replaces.
      Promise.resolve(saveCostTableDraft({ token, role: r, id, rows: payload })).catch(
        (err) => console.error("Could not flush the cost table draft:", err)
      );
      pendingRef.current = null;
    };
  }, []);

  /* The stored draft's own timestamp arrives with the quote, one fetch after
     first render. Adopt it so a table the operator has not touched still shows
     when it was last saved — but never let it overwrite a save made since. */
  useEffect(() => {
    if (!initialSavedAt) return;
    setLastSavedAt((prev) => prev || new Date(initialSavedAt));
  }, [initialSavedAt]);

  // The green tick is a moment, not a state.
  useEffect(() => {
    if (!justSaved) return undefined;
    const t = setTimeout(() => setJustSaved(false), savedFlashMs);
    return () => clearTimeout(t);
  }, [justSaved, savedFlashMs]);

  /** The operator's explicit instruction. Always writes, even when clean — a
   *  Save button that silently no-ops reads as a broken Save button. */
  const saveNow = useCallback(() => {
    setJustSaved(false);
    return write({ manual: true });
  }, [write]);

  const status = saving
    ? "saving"
    : error
      ? "error"
      : isDirty
        ? "dirty"
        : justSaved
          ? "saved"
          : "clean";

  return { status, isDirty, lastSavedAt, error, saveNow, canPersist };
}
